import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { TablerIconsModule } from 'angular-tabler-icons';
import { ToastrService } from 'ngx-toastr';
import { DeviceControlService } from 'src/app/services/device-control.service';
import { ConfirmDialogComponent } from 'src/app/pages/apps/confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-engine-restart-button',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatMenuModule,
    MatTooltipModule,
    TablerIconsModule,
  ],
  template: `
    <button
      mat-stroked-button
      color="warn"
      [matMenuTriggerFor]="restartMenu"
      matTooltip="Opciones de reinicio del dispositivo"
      [disabled]="isRestarting"
    >
      <i-tabler
        [name]="isRestarting ? 'loader-2' : 'rotate-clockwise'"
        class="icon-18 m-r-4 d-flex"
        [class.spin]="isRestarting"
      ></i-tabler>
      <span>{{ isRestarting ? 'Reiniciando...' : 'Reiniciar motor' }}</span>
    </button>

    <mat-menu #restartMenu="matMenu">
      <!-- Opción activa: reiniciar motor ALPR -->
      <button mat-menu-item (click)="confirmRestartEngine()" [disabled]="isRestarting">
        <div class="restart-option">
          <i-tabler name="rotate-clockwise" class="icon-20 d-flex option-icon active"></i-tabler>
          <div class="option-text">
            <span class="option-label">Reiniciar motor ALPR (~15s)</span>
            <span class="option-subtitle">libera memoria GPU</span>
          </div>
        </div>
      </button>

      <!-- Opción deshabilitada: reiniciar host completo (Fase 2) -->
      <button
        mat-menu-item
        disabled
        matTooltip="Funcionalidad disponible en Fase 2"
        matTooltipPosition="right"
      >
        <div class="restart-option">
          <i-tabler name="lock" class="icon-20 d-flex option-icon locked"></i-tabler>
          <div class="option-text">
            <span class="option-label">Reiniciar Jetson (~60s)</span>
            <span class="option-subtitle">próximamente (Fase 2)</span>
          </div>
        </div>
      </button>
    </mat-menu>
  `,
  styles: [`
    .restart-option {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 2px 0;
    }

    .option-icon {
      flex-shrink: 0;
    }

    .option-icon.active {
      color: #e65100;
    }

    .option-icon.locked {
      color: #9e9e9e;
    }

    .option-text {
      display: flex;
      flex-direction: column;
      line-height: 1.3;
    }

    .option-label {
      font-size: 13px;
      font-weight: 500;
    }

    .option-subtitle {
      font-size: 11px;
      color: #757575;
    }

    .spin {
      animation: spin 1s linear infinite;
    }

    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
  `]
})
export class EngineRestartButtonComponent {
  isRestarting = false;

  constructor(
    private deviceControlService: DeviceControlService,
    private dialog: MatDialog,
    private toastr: ToastrService
  ) {}

  confirmRestartEngine(): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '440px',
      data: {
        title: 'Reiniciar motor ALPR',
        message:
          'Se reiniciará el motor para liberar memoria GPU. La detección se interrumpe ~15 segundos y vuelve sola. ¿Continuar?',
        confirmText: 'Sí, reiniciar',
        cancelText: 'Cancelar',
        confirmColor: 'warn',
      },
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.doRestartEngine();
      }
    });
  }

  private doRestartEngine(): void {
    this.isRestarting = true;

    this.deviceControlService.restartDevice('restart-engine').subscribe({
      next: (result) => {
        this.isRestarting = false;
        if (result.success) {
          this.toastr.success(
            'Reiniciando el motor para liberar memoria GPU (~15s)',
            'Motor reiniciando',
            { timeOut: 8000 }
          );
        } else {
          // El backend respondió 200 pero con success:false (ej. NOT_IMPLEMENTED)
          const msg = result.error || 'El dispositivo no pudo procesar la solicitud.';
          this.toastr.warning(msg, 'Advertencia');
        }
      },
      error: (err) => {
        this.isRestarting = false;
        if (err.status === 504) {
          this.toastr.error(
            'El dispositivo no respondió (timeout). Verificá que la Jetson esté conectada.',
            'Sin respuesta'
          );
        } else {
          const msg =
            err.error?.error ||
            err.error?.message ||
            err.message ||
            'Error desconocido al reiniciar';
          this.toastr.error(msg, 'Error al reiniciar');
        }
      },
    });
  }
}
